import {
  Bytes,
  FieldValue,
  FirebaseFirestore,
} from '@capacitor-firebase/firestore';
import cloneDeepWith from 'lodash/cloneDeepWith';
import omit from 'lodash/omit';
import { toast } from 'react-toastify';
import { ApplicationError } from 'features/error-handling/ApplicationError';
import {
  convertToApplicationError,
  isFirebasePermissionDeniedError,
} from 'features/error-handling/errorUtils';
import { getTraceProperties } from 'features/firebase/firebase.utils';
import i18n from 'features/i18n/i18n';
import { logger } from 'features/logger/logger';
import {
  addPerformanceStartMark,
  addPerformanceEndMark,
} from 'features/monitoring/performanceMonitor';
import type {
  DbTask,
  DbTaskDataForUpdate,
  DbTaskDataForUpdateOnScreenLoad,
  DbTaskDataForUpdateOnScreenPostQuestionnaireDone,
  DbTraceProperties,
} from '@hub/common';
import type {
  BrowserInfo,
  DeviceInfo,
  ProseInfo,
} from 'features/system-info/store/systemInfoSlice';

/**
 * Recursively strips `undefined` values from a plain object.
 * Preserves `Uint8Array` / typed arrays (binary data) and `null`.
 */
const deepRemoveUndefined = (
  obj: Record<string, unknown>
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined) continue;
    if (Array.isArray(value)) {
      result[key] = value.map((item: unknown) => {
        if (
          item !== null &&
          typeof item === 'object' &&
          !ArrayBuffer.isView(item)
        ) {
          return deepRemoveUndefined(item as Record<string, unknown>);
        }
        return item;
      });
    } else if (
      value !== null &&
      typeof value === 'object' &&
      !ArrayBuffer.isView(value)
    ) {
      result[key] = deepRemoveUndefined(value as Record<string, unknown>);
    } else {
      result[key] = value;
    }
  }
  return result;
};

/**
 * Deeply strips `undefined` values from `contextData`.
 * PROSE protobuf deserialization sets unset RenderContext fields to undefined
 * at any nesting depth, which Firestore rejects.
 */
export const removeUndefinedContextDataFields = <T extends DbTaskDataForUpdate>(
  data: T
): T => {
  if (!('contextData' in data) || !data.contextData) return data;

  return {
    ...data,
    contextData: deepRemoveUndefined(
      data.contextData as unknown as Record<string, unknown>
    ),
  };
};

// cloneDeepWith returns `any` when a customizer is supplied; keep the input type.
const cloneContextData = <R>(
  contextData: R,
  customizer: (value: unknown) => unknown
): R => cloneDeepWith(contextData, customizer) as R;

/**
 * Wraps every `Uint8Array` in `contextData` in the plugin's `Bytes` class
 * before a write, so Firestore stores it as a native binary value. A raw
 * `Uint8Array` is serialized field-by-field by the `@capacitor-firebase/firestore`
 * bridge into a numeric-keyed object, which no longer round-trips as binary
 * and breaks PROSE re-encoding on the next render. PROSE holds opaque state
 * (`internalState.data`) as `Uint8Array`, so it must survive persistence
 * intact. Native binary also keeps the document legible to admin-SDK
 * consumers (Cloud Functions read the field back as a `Buffer`).
 */
export const serializeContextDataBytes = <T extends DbTaskDataForUpdate>(
  data: T
): T => {
  if (!('contextData' in data) || !data.contextData) return data;

  return {
    ...data,
    contextData: cloneContextData(data.contextData, value =>
      value instanceof Uint8Array ? Bytes.fromUint8Array(value) : undefined
    ),
  };
};

/**
 * Restores `Uint8Array` values from the `Bytes` instances the plugin returns
 * for native binary fields when reading an activity task document back from
 * Firestore.
 */
export const deserializeContextDataBytes = (data: DbTask): DbTask => {
  if (!data.contextData) return data;

  return {
    ...data,
    contextData: cloneContextData(data.contextData, value =>
      value instanceof Bytes ? value.toUint8Array() : undefined
    ),
  };
};

/**
 * A task document as the browser holds it: `lastActivityAt` removed (the
 * browser never reads it, and Redux holds no Timestamp) and binary context
 * state restored.
 */
export const toClientActivityTask = (
  data: DbTask & { lastActivityAt?: unknown }
): DbTask => deserializeContextDataBytes(omit(data, 'lastActivityAt'));

/**
 * Prepares activity task document data for a Firestore write: strips undefined
 * `contextData` fields and wraps binary state in `Bytes`. Every writer of
 * `contextData` must funnel through this so opaque PROSE state survives the
 * round-trip.
 */
export const prepareActivityTaskWriteData = <T extends DbTaskDataForUpdate>(
  data: T
): T => serializeContextDataBytes(removeUndefinedContextDataFields(data));

export const getActivityTasksCollectionPath = (
  userId: string,
  participationId: string
) => `userData/${userId}/participations/${participationId}/tasks`;

export const getActivityTaskDocumentPath = (
  userId: string,
  participationId: string,
  activityName: string
) =>
  `${getActivityTasksCollectionPath(userId, participationId)}/${activityName}`;

export const getActivityTaskHistoryEntriesPath = (
  userId: string,
  participationId: string,
  activityName: string
) =>
  `userData/${userId}/participations/${participationId}/tasksHistory/${activityName}/entries`;

type ActivityTaskUpdateData =
  | DbTaskDataForUpdate
  | DbTaskDataForUpdateOnScreenLoad
  | DbTaskDataForUpdateOnScreenPostQuestionnaireDone;

/**
 * The single browser write path for task documents. The rules require every
 * update to set `lastActivityAt` to the server's request time.
 */
export const updateActivityTaskDocument = (
  reference: string,
  data: ActivityTaskUpdateData
): Promise<void> =>
  FirebaseFirestore.updateDocument({
    reference,
    data: { ...data, lastActivityAt: FieldValue.serverTimestamp() },
  });

export const loadActivityTask = async (
  userId: string,
  participationId: string,
  activityName: string
): Promise<DbTask> => {
  try {
    addPerformanceStartMark('questionnaireDocumentLoad');

    const { snapshot } = await FirebaseFirestore.getDocument({
      reference: getActivityTaskDocumentPath(
        userId,
        participationId,
        activityName
      ),
    });

    if (!snapshot.data) {
      throw new Error(
        `Data is missing in loaded activity task document snapshot (userId: ${userId}, participationId: ${participationId}, activityName: ${activityName})`
      );
    }

    return toClientActivityTask(snapshot.data as DbTask);
  } catch (error) {
    if (isFirebasePermissionDeniedError(error)) {
      const appError = new ApplicationError('PHW-F-001');
      appError.cause = error;
      throw appError;
    }
    throw error;
  } finally {
    addPerformanceEndMark('questionnaireDocumentLoad', {
      console: true,
      datadog: true,
    });
  }
};

export const updateActivityTask = ({
  userId,
  participationId,
  activityName,
  windowId,
  language,
  proseInfo,
  browserInfo,
  deviceInfo,
  data,
}: {
  userId: string;
  participationId: string;
  activityName: string;
  windowId: string;
  language: string;
  proseInfo: ProseInfo;
  browserInfo: BrowserInfo;
  deviceInfo: DeviceInfo;
  data: DbTaskDataForUpdate;
}): void => {
  try {
    addPerformanceStartMark('questionnaireDocumentUpdate');

    const extendedData: DbTaskDataForUpdate & Required<DbTraceProperties> = {
      ...data,
      ...getTraceProperties({
        now: new Date(),
        windowId,
        language,
        proseInfo,
        browserInfo,
        deviceInfo,
      }),
    };

    void updateActivityTaskDocument(
      getActivityTaskDocumentPath(userId, participationId, activityName),
      // Firestore rejects undefined values (PROSE leaves unset RenderContext fields
      // undefined) and mangles raw Uint8Array binary state; prepareActivityTaskWriteData
      // strips the former and wraps the latter in Bytes.
      prepareActivityTaskWriteData(extendedData)
    ).catch((error: unknown) => {
      let appError: ApplicationError;

      if (isFirebasePermissionDeniedError(error)) {
        appError = new ApplicationError('PHW-F-001');
        appError.cause = error;
      } else {
        appError = convertToApplicationError(error);
      }

      logger.error('Error in questionnaireDocumentUpdate', undefined, appError);
      toast.error(i18n.t('QuestionnaireUpdateFailureMessage'));
    });
  } finally {
    addPerformanceEndMark('questionnaireDocumentUpdate', {
      console: true,
      detail: { actionName: data.traceClientActionType },
    });
  }
};
